import React, { createContext, useContext, useState, useCallback } from 'react';
import { CheckCircle, AlertCircle, Info, X } from 'lucide-react';

const NotificationContext = createContext(null);

const typeStyles = {
  success: {
    bg: 'bg-emerald-50 dark:bg-emerald-900/30',
    border: 'border-emerald-200 dark:border-emerald-800',
    text: 'text-emerald-700 dark:text-emerald-300',
    icon: CheckCircle,
    bar: 'bg-emerald-500',
  },
  error: {
    bg: 'bg-red-50 dark:bg-red-900/30',
    border: 'border-red-200 dark:border-red-800',
    text: 'text-red-700 dark:text-red-300',
    icon: AlertCircle,
    bar: 'bg-red-500',
  },
  warning: {
    bg: 'bg-yellow-50 dark:bg-yellow-900/30',
    border: 'border-yellow-200 dark:border-yellow-800',
    text: 'text-yellow-700 dark:text-yellow-300',
    icon: AlertCircle,
    bar: 'bg-yellow-500',
  },
  info: {
    bg: 'bg-blue-50 dark:bg-blue-900/30',
    border: 'border-blue-200 dark:border-blue-800',
    text: 'text-blue-700 dark:text-blue-300',
    icon: Info,
    bar: 'bg-blue-500',
  },
};

function NotificationToast({ id, type, message, duration, onRemove }) {
  const ts = typeStyles[type] || typeStyles.info;
  const Icon = ts.icon;

  React.useEffect(() => {
    if (duration > 0) {
      const timer = setTimeout(() => onRemove(id), duration);
      return () => clearTimeout(timer);
    }
  }, [id, duration, onRemove]);

  return (
    <div className={`${ts.bg} ${ts.border} border rounded-xl shadow-xl max-w-sm overflow-hidden animate-in fade-in slide-in-from-top-2`}>
      <div className="p-4 flex items-start gap-3">
        <div className="mt-0.5 flex-shrink-0">
          <Icon size={20} className={ts.text} />
        </div>
        <div className="flex-1">
          <p className={`text-sm font-medium ${ts.text}`}>{message}</p>
        </div>
        <button onClick={() => onRemove(id)} className="flex-shrink-0 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors">
          <X size={18} />
        </button>
      </div>
      {duration > 0 && (
        <div className={`h-1 w-full ${ts.bar} bg-opacity-20`}>
          <div className={`h-full ${ts.bar}`} style={{ animation: `notifyshrink ${duration}ms linear forwards` }} />
          <style>{`@keyframes notifyshrink{from{width:100%}to{width:0%}}`}</style>
        </div>
      )}
    </div>
  );
}

export function NotificationProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const notify = useCallback((message, type = 'success', duration = 4000) => {
    const id = Date.now() + Math.random();
    setToasts(prev => [...prev, { id, type, message, duration }]);
  }, []);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  return (
    <NotificationContext.Provider value={{ notify, success: (msg) => notify(msg, 'success'), error: (msg) => notify(msg, 'error'), warning: (msg) => notify(msg, 'warning'), info: (msg) => notify(msg, 'info') }}>
      {children}
      <div className="fixed top-4 right-4 z-[9999] flex flex-col gap-2">
        {toasts.map(t => (
          <NotificationToast key={t.id} {...t} onRemove={removeToast} />
        ))}
      </div>
    </NotificationContext.Provider>
  );
}

export function useNotification() {
  const ctx = useContext(NotificationContext);
  if (!ctx) {
    // Fallback: return a dummy that uses console.log
    return { notify: (msg, type) => console.log(`[${type}] ${msg}`) };
  }
  return ctx;
}
